import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useAudio } from "../context/AudioContext";

const readDuration = (file) => new Promise((res) => {
  const a = document.createElement("audio");
  a.preload = "metadata";
  a.onloadedmetadata = () => { res(Number.isFinite(a.duration) ? a.duration : 0); URL.revokeObjectURL(a.src); };
  a.onerror = () => res(0);
  a.src = URL.createObjectURL(file);
});

const field = "w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--bone)] outline-none py-3 f-serif text-lg placeholder:text-[#7d7d77]";

export default function TrackUploader() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", version: "", kind: "Song", lyrics: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [pct, setPct] = useState(null);
  const formRef = useRef(null);
  const { reloadTracks } = useAudio();

  const load = () => api.get("/admin/tracks").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!audio || !form.title.trim()) return toast("Add a title and an audio file.");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("duration", String(await readDuration(audio)));
    fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    setPct(0);
    try {
      await api.post("/admin/tracks", fd, { timeout: 600000, onUploadProgress: (p) => setPct(p.total ? Math.round((p.loaded / p.total) * 100) : 0) });
      toast(`${form.title} is now in the listening room.`);
      setForm({ title: "", version: "", kind: "Song", lyrics: "" }); setAudio(null); setCover(null); formRef.current?.reset();
      load(); reloadTracks();
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed. Try again.");
    }
    setPct(null);
  };

  const remove = async (slug) => {
    await api.delete(`/admin/tracks/${slug}`);
    toast("Removed from the listening room.");
    load(); reloadTracks();
  };

  return (
    <section className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="admin-track-uploader">
      <div className="lg:col-span-5">
        <p className="eyebrow mb-4">Media</p>
        <h2 className="f-display font-black uppercase text-5xl leading-[0.9]">Add a track</h2>
        <p className="text-[#cfcec8] mt-4">Upload audio (MP3, WAV, M4A up to 80 MB) and an optional cover. It appears in Listen straight away.</p>
        <ul className="mt-10 border-t border-[var(--bone)]" data-testid="admin-uploaded-list">
          {list.map((t) => (
            <li key={t.slug} className="border-b border-[var(--line)] py-4 flex items-center gap-4">
              <img src={t.cover} alt="" className="w-12 h-12 object-cover grayscale" />
              <span className="flex-1 min-w-0">
                <span className="block f-display font-bold uppercase text-xl leading-none truncate">{t.title}</span>
                <span className="block f-mono text-[11px] text-[var(--ash)] mt-1">{t.version} &middot; {fmt(t.duration)}</span>
              </span>
              <button onClick={() => remove(t.slug)} className="p-2 text-[var(--ash)] hover:text-[var(--signal)]" aria-label="Remove" data-testid={`admin-remove-${t.slug}`}><Trash2 size={16} /></button>
            </li>
          ))}
          {!list.length && <li className="py-4 f-serif italic text-[#bdbcb5]">No uploaded tracks yet.</li>}
        </ul>
      </div>
      <form ref={formRef} onSubmit={submit} className="lg:col-span-7 space-y-6 border border-[var(--line)] p-5 sm:p-8" data-testid="admin-track-form">
        <input className={field} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-track-title" />
        <input className={field} placeholder="Version or subtitle (optional)" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="admin-track-version" />
        <div className="flex gap-2">
          {["Song", "Spoken Reading"].map((k) => (
            <button type="button" key={k} onClick={() => setForm({ ...form, kind: k })} className={`f-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border ${form.kind === k ? "bg-[var(--bone)] text-[var(--ink)] border-[var(--bone)]" : "border-[var(--line)]"}`} data-testid={`admin-track-kind-${k.toLowerCase().replace(" ", "-")}`}>{k}</button>
          ))}
        </div>
        <label className="block">
          <span className="eyebrow">Audio file</span>
          <input type="file" accept="audio/*" onChange={(e) => setAudio(e.target.files?.[0] || null)} className="block mt-2 f-mono text-xs file:mr-4 file:border file:border-[var(--bone)] file:bg-transparent file:text-[var(--bone)] file:px-4 file:py-2 file:f-mono" data-testid="admin-track-audio" />
        </label>
        <label className="block">
          <span className="eyebrow">Cover image (optional)</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setCover(e.target.files?.[0] || null)} className="block mt-2 f-mono text-xs file:mr-4 file:border file:border-[var(--bone)] file:bg-transparent file:text-[var(--bone)] file:px-4 file:py-2" data-testid="admin-track-cover" />
        </label>
        <textarea className={`${field} min-h-[140px] resize-y`} placeholder="Lyrics or transcript (optional)" value={form.lyrics} onChange={(e) => setForm({ ...form, lyrics: e.target.value })} data-testid="admin-track-lyrics" />
        <button type="submit" disabled={pct !== null} className="flex items-center gap-3 f-mono text-xs uppercase tracking-[0.2em] border border-[var(--bone)] px-6 py-3 hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors disabled:opacity-60" data-testid="admin-track-submit">
          <Upload size={14} /> {pct === null ? "Upload track" : pct < 100 ? `Uploading ${pct}%` : "Saving\u2026"}
        </button>
      </form>
    </section>
  );
}
